
import { useContext, useEffect, useRef } from 'react';
import { AuthContext } from '@/context/AuthContext';
import { isUserInTeam, getPrimaryTeamId } from '@/context/types';
import { refreshTokens } from "@/integrations/auth/http";

export function useAuth() {
  const context = useContext(AuthContext);
  const initRef = useRef(false);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  // User profile state exposure
  const user = context.session?.user || null;
  const userProfile = context.userProfile;
  const teamInfo = context.teamInfo;
  
  // Log authentication state for debugging
  useEffect(() => {
    if (context.debugMode) {
      console.log("Auth hook state:", {
        isAuthenticated: !!(
          context.session?.user?.id && context.session?.access_token
        ),
        isLoading: context.isLoading,
        hasSession: !!context.session,
        hasUser: !!context.session?.user,
        userId: context.session?.user?.id,
        hasToken: !!context.session?.access_token,
        tokenExpiry: context.session?.expires_at ? new Date(context.session.expires_at * 1000).toISOString() : null,
        userProfile: !!userProfile,
        hasTeam: !!teamInfo && (Array.isArray(teamInfo) ? teamInfo.length > 0 : !!teamInfo),
        teamsCount: Array.isArray(teamInfo) ? teamInfo.length : (teamInfo ? 1 : 0),
      });
    }
    
    // Run once on initial mount to check auth state
    if (!initRef.current) {
      initRef.current = true;
      // Only refresh if we have a session but no user profile
      if (context.session?.user && !userProfile && !context.isLoading) {
        context.refreshUserProfile().catch(err => {
          console.error("Initial profile refresh error:", err);
        });
      }
    }
  }, [context.session, context.isLoading, userProfile, teamInfo, context.debugMode]);
  
  // Add a utility function to check if token is expired or will expire soon
  const isTokenExpiredOrExpiring = () => {
    if (!context.session?.expires_at) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const expiresAt = context.session.expires_at;
    
    // Consider token expiring if less than 5 minutes left
    const isExpiringSoon = expiresAt - currentTime < 300;
    
    return expiresAt < currentTime || isExpiringSoon;
  };
  
  // Role check helpers
  const isAdmin = !!userProfile?.role && userProfile.role === 'admin';
  const isRegularUser = !!userProfile?.role && userProfile.role === 'user';
  // Team membership is determined by presence in team_users table
  const isTeamMember = !!teamInfo && (Array.isArray(teamInfo) ? teamInfo.length > 0 : true);
  
  // Helper function to check if user belongs to a specific team
  const isInTeam = (teamId: string): boolean => isUserInTeam(teamInfo, teamId);
  
  // Get primary team ID (first team in the array)
  const getPrimaryTeam = (): string | null => getPrimaryTeamId(teamInfo);
  
  const forceRefreshSession = async () => {
    try {
      if (context.debugMode) console.log("Force refreshing session and profile");
      const t = await refreshTokens();
      if (!t) return false;
      await context.refreshUserProfile();
      return true;
    } catch (error) {
      console.error("Error during forced session refresh:", error);
      return false;
    }
  };
  
  return {
    session: context.session,
    user,
    userProfile,
    teamInfo,
    isLoading: context.isLoading,
    isAuthenticated: !!(
      context.session?.user?.id && context.session?.access_token
    ),
    isAdmin,
    isTeamMember,
    isInTeam,
    getPrimaryTeam,
    isRegularUser,
    isTokenExpiredOrExpiring,
    refreshUserProfile: context.refreshUserProfile,
    forceRefreshSession,
    setUserProfile: context.setUserProfile,
    signOut: context.signOut,
    debugMode: context.debugMode,
    setDebugMode: context.setDebugMode,
    setSession: context.setSession,
  };
}
